// bnz-work-diary.ts
import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum BNZWorkDiaryEmployeeTimesheetKeys {
  employeeName = 'employeeName',
  startTime = 'startTime',
  endTime = 'endTime',
  breakTime = 'breakTime', // in minutes
}

export enum BNZWorkDiaryToolsKeys {
  toolName = 'toolName',
  startMileage = 'startMileage',
  endMileage = 'endMileage',
  fuelGauge = 'fuelGauge', // in liters
  deliveryCertificate = 'deliveryCertificate',
}

export enum BNZWorkDiaryVehiclesKeys {
  vehicleName = 'vehicleName',
  fromLocation = 'fromLocation',
  toLocation = 'toLocation',
  leading = 'leading',
}

export enum BNZWorkDiaryKeepingKeys {
  keepingName = 'keepingName',
  startTime = 'startTime',
  endTime = 'endTime',
  guardNames = 'guardNames',
  notes = 'notes',
  photoID = 'photoID',
}

export const BNZWorkDiaryFileDataSchema = yup.object().shape({
  siteName: yup.string().nullable().optional(),
  submittedAt: yup.string().nullable().optional(),
  submittedBy: yup.string().nullable().optional(),
  activityDescription: yup.string().nullable().optional(),
  weatherConditions: yup.string().nullable().optional(),
  reportDate: yup.string().nullable().optional(),
  foremanName: yup.string().nullable().optional(),
  foremanSignature: yup.string().nullable().optional(),
  comments: yup.string().nullable().optional(),
  customerRepresentativeName: yup.string().nullable().optional(),
  customerRepresentativeSignature: yup.string().nullable().optional(),
  tools: yup
    .array()
    .of(
      yup.object().shape({
        [BNZWorkDiaryToolsKeys.toolName]: yup.string().nullable().optional(),
        [BNZWorkDiaryToolsKeys.startMileage]: yup.string().nullable().optional(),
        [BNZWorkDiaryToolsKeys.endMileage]: yup.string().nullable().optional(),
        [BNZWorkDiaryToolsKeys.fuelGauge]: yup.string().nullable().optional(),
        [BNZWorkDiaryToolsKeys.deliveryCertificate]: yup.string().nullable().optional(),
      }),
    )
    .required(),
  vehicles: yup
    .array()
    .of(
      yup.object().shape({
        [BNZWorkDiaryVehiclesKeys.vehicleName]: yup.string().nullable().optional(),
        [BNZWorkDiaryVehiclesKeys.fromLocation]: yup.string().nullable().optional(),
        [BNZWorkDiaryVehiclesKeys.toLocation]: yup.string().nullable().optional(),
        [BNZWorkDiaryVehiclesKeys.leading]: yup.string().nullable().optional(),
      }),
    )
    .required(),
  keeping: yup
    .array()
    .of(
      yup.object().shape({
        [BNZWorkDiaryKeepingKeys.keepingName]: yup.string().nullable().optional(),
        [BNZWorkDiaryKeepingKeys.startTime]: yup.string().nullable().optional(),
        [BNZWorkDiaryKeepingKeys.endTime]: yup.string().nullable().optional(),
        [BNZWorkDiaryKeepingKeys.guardNames]: yup.string().nullable().optional(),
        [BNZWorkDiaryKeepingKeys.notes]: yup.string().nullable().optional(),
        [BNZWorkDiaryKeepingKeys.photoID]: yup.string().nullable().optional(),
      }),
    )
    .required(),
  employeeTimesheet: yup
    .array()
    .of(
      yup.object().shape({
        [BNZWorkDiaryEmployeeTimesheetKeys.employeeName]: yup.string().nullable().optional(),
        [BNZWorkDiaryEmployeeTimesheetKeys.startTime]: yup.string().nullable().optional(),
        [BNZWorkDiaryEmployeeTimesheetKeys.endTime]: yup.string().nullable().optional(),
        [BNZWorkDiaryEmployeeTimesheetKeys.breakTime]: yup.string().nullable().optional(),
      }),
    )
    .required(),
})

export type BNZWorkDiaryFileData = yup.InferType<typeof BNZWorkDiaryFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<BNZWorkDiaryFileData>

export const BNZWorkDiaryFileMappings: RequiredFileData = {
  siteName: 'a2e4bf58-db0e-46b5-9ce0-ca8c6403a54f',
  submittedAt: '34195652-a05c-4008-a93a-0a7bc4272ba0',
  submittedBy: '1055c6d7-5fb7-4cd6-95b3-9c15f06ed635',
  activityDescription: 'ab4568f9-6194-44d0-a4cb-eaff3c64b8a1',
  weatherConditions: 'a71a8a2a-4dda-43a4-bc51-eba78da08fef',
  reportDate: '0f183842-29c7-4d0e-a64c-458babfb7dc2',
  foremanName: '6827665f-23ee-49d4-87cf-5287685ed9f6',
  foremanSignature: 'a381cd61-8d0f-4716-a734-8711ab5d74e4',
  comments: '5bc402a9-51c8-4caa-836c-b9adca69eaa6',
  customerRepresentativeName: 'f7d1c034-3735-4cdb-8bbd-d0c9b1c5a429',
  customerRepresentativeSignature: '539f2834-1716-41a8-af9d-c3235a89d0bb',
  tools: [
    {
      toolName: 'df61493b-889c-4e91-8ec2-e6018b3ee7d9',
      startMileage: 'be565e1c-8fb6-4a4f-9d02-07fb5bbac0d0',
      endMileage: 'b1b3e75b-5f3c-49f9-9073-7eab7e845da8',
      fuelGauge: '6f694b13-903f-4e6a-8676-db2a1730ed06',
      deliveryCertificate: '24fd13d6-c1a9-42d9-b907-bafafc80d55a',
    },
  ],
  vehicles: [
    {
      vehicleName: '350cd853-b83b-4ff6-90d3-0e8d9ead6ca5',
      fromLocation: 'e4c8c5f4-5d4b-4c0a-8d5f-4d3d2c5b1c5d',
      toLocation: '48466283-5c7a-478b-b2fd-199a61e254ee',
      leading: 'f279e452-b0b0-4074-92f1-87070c5ae3e7',
    },
  ],
  keeping: [
    {
      keepingName: 'b69d7de8-3461-4f0d-a0b4-c6c027a18ae6',
      startTime: 'ed882ebc-c082-477f-a49d-31b596016d49',
      endTime: 'e55e8fe9-852b-4b24-8ecd-6883a6f48cb2',
      guardNames: '867a7ee6-b94d-4517-a59f-8f28b05bbe07',
      notes: '016cb2ae-8de1-4343-9017-8569ea2e47ac',
      photoID: '41da1f84-7878-4f86-9dee-9d2d88c116e1',
    },
  ],
  employeeTimesheet: [
    {
      employeeName: '761e9e74-ec05-4513-a5ae-0f7f7b37a50a',
      startTime: '20cd186d-7498-4b54-9d49-2a44254bd825',
      endTime: '7436813f-a784-4e22-97f4-e2acac3a0f70',
      breakTime: '2864b027-e616-4b7b-b0b0-ef4b99558e03',
    },
  ],
}
